import { forwardRef, useImperativeHandle } from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import {
  Select,
  Row,
  Col,
  Tag,
  message,
  Flex,
  Spin,
  Form,
  Table,
  InputNumber,
  Card,
  Breadcrumb,
  Dropdown,
  Space,
  Typography,
  Popover,
  Timeline,
  Input,
  notification,
  Modal as AntModal,
} from "antd";
import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import dayjs from "dayjs";
import "react-quill/dist/quill.snow.css";
import {
  Alert,
  Badge,
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect, {
  SelectOption,
} from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { projectsApis } from "../../../api/projects";
import { useSelector, useDispatch } from "react-redux";
import { Icon } from "../../../components/Component";
import Swal from "sweetalert2";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownOutlined,
  LoadingOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { RootState } from "../../../store/store";
import { tasksApis } from "../../../api/NewTasks";
import {
  resetIssueInDetailModalSlice,
  updateIssueInDetailModalSlice,
} from "../../../store/slices/projectManagement/issuee-view/IssueInDetailView";
import { tagRender } from "../projects/projectCreation";
import { CommentsTab } from "../../../components/comments/Comments";
import { issueApis } from "../../../api/Issue";
import { phaseApis } from "../../../api/Phase";
import debounce from "lodash.debounce";
import LinkIssueTable from "./LinkIssueTable";
import IssueResolutionTab from "./IssueResolutionTab";
import { TimeLogTable } from "../timeSheets/TimeLogTable";
import { useLocation } from "react-router";
import { workApprovalApis } from "../../../api/WorkApproval";
import IssueStatusTimeLine from "./IssuesStatusTimeLine";
import DocumentsTabWrapper from "../projectDocument/DocumentsTabWrapper";
import {
  EditAccess,
  CancelAccess,
  ApproveAccess,
} from "../../../components/accessControl/AccessControl";
import IssueActivityStreamLine from "./IssueActivityTimeLine";
import IssueApprovalDrawer from "./IssueApprovalDrawer";
import { useWbsName } from "../shared/useWbsCodes";

export type TIssueStatus =
  | "Open"
  | "InProgress"
  | "Resolved"
  | "Closed"
  | "Reopen"
  | "OnHold";

interface ProjectUser {
  userId: string;
  userName: string;
  projectUserId: number;
  roleName: string;
}

interface Priority {
  priorityName: string;
  priorityId: number;
}
export interface UpdateIssuePayload {
  projectId: number;
  issueId: number;
  activityId?: number;
  taskId?: number;
  issueName: string;
  issueDescription?: string;
  issueCriteria?: string;
  issueType: string;
  issueStartDate?: string;
  deadlineDate?: string;
  linkedTask?: number;
  flag?: string;
  module?: string;
  severity?: number | null;
  affectedPhaseId?: number;
  releasePhaseId?: number;
  classification?: string;
  reproducible?: boolean;
  assignedTo?: number;
  issueStatus?: TIssueStatus;
  priorityId?: number;
  issueTags: {
    newLines: { tagId?: number; tagName: string }[];
    deletedLines: { issueTagId: number }[];
  };
  issueLinks?: {
    newLines: { linkedIssueId: number; linkType: string }[];
    deletedLines: { issueLinkId: number }[];
  };
  issueResolutions?: {
    newLines: { resolutionText: string; resolutionStatus: TIssueStatus }[];
    deletedLines: { issueResolutionId: number }[];
  };
  reminderFlag?: boolean;
  reminders?: {
    reminderId?: number;
    reminderType: string;
    specificDate?: string;
    specificTime?: string;
    daysOffset?: number;
    offsetFrom?: string;
    recurrenceType: string;
    recurrenceInterval?: number;
    maxRecurrenceCount?: number;
    moduleType: string;
    reminderUsers: {
      newLines: { userId: number }[];
      deletedLines: { reminderUserId: number }[];
    };
  };
  description: string;
  approvalRequired?: boolean;
  autoApprovalRemarks?: string;
  stateApprovalHierarchy?: {
    newLines?: {
      userId: number;
      roleId: number;
      level: string;
      approvalStage: string;
    }[];
    updatedLines?: {
      stateApprovalHierarchyId: number;
      approvalStage: string;
    }[];
    deletedLines?: {
      stateApprovalHierarchyId: number;
    }[];
  };
}

export interface IssueFormFields {
  projectId: number;
  issueId: number;
  issueName: string;
  issueType: string;
  issueStatus: TIssueStatus;
  issueDescription: string;
  assignedTo: number | null;
  issueStartDate: dayjs.Dayjs | undefined;
  deadlineDate: dayjs.Dayjs | undefined;
  priorityId: number | null;
  linkedTask?: number | null;
  releasePhaseId?: number | null;
  issueTags: Array<{ tagId: number; tagName: string }>;
  issueLinks: Array<{ linkedIssueId: number | null }>;
  reminderFlag: boolean;
  flag?: string;
  severity: string | null;
  description: string;
}

export const formatRecurrence = ({
  recurrenceType,
  recurrenceInterval,
  maxRecurrenceCount,
}: any) => {
  if (recurrenceType === "None") {
    return "No recurrence";
  }
  const unit: any = {
    Daily: "day(s)",
    Weekly: "week(s)",
    Monthly: "month(s)",
    Yearly: "year(s)",
  };
  const intervalText = `Every ${recurrenceInterval} ${unit?.[recurrenceType]}`;
  const countText = `Ends after ${maxRecurrenceCount} occurrence${
    maxRecurrenceCount > 1 ? "s" : ""
  }`;
  return `${intervalText}, ${countText}`;
};

const StatusWithInfo = ({
  currentStatus,
  previousStatus,
  infoText,
}: {
  currentStatus: string;
  previousStatus?: string;
  infoText: string;
}) => {
  if (!previousStatus) {
    return <span>{currentStatus}</span>;
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <span>{currentStatus}</span>
      <Popover
        content={
          <div style={{ padding: "8px" }}>
            <div>
              {infoText}: {previousStatus}
            </div>
          </div>
        }
        trigger="hover"
      >
        <Icon
          name="info"
          style={{
            cursor: "pointer",
            marginLeft: "8px", // Add space between status and icon
            color: "#2557d6", // Match the status color
          }}
        />
      </Popover>
    </div>
  );
};

export interface IssueInformationByIdRef {
  resetEditMode: () => void;
}

const IssueInformationById = forwardRef<
  IssueInformationByIdRef,
  {
    refreshData: any;
    handleClose: () => any;
    isFromWorkUI?: boolean;
    hideInternalTabs?: boolean;
    initialEditMode?: boolean;
    onBackToSummary?: () => void;
  }
>(({
  refreshData,
  handleClose,
  isFromWorkUI,
  hideInternalTabs = false,
  initialEditMode = false,
  onBackToSummary,
}, ref) => {
  // console.log("isFromWorkUI:", isFromWorkUI);

  // Expose the resetEditMode method to parent via ref
  useImperativeHandle(ref, () => ({
    resetEditMode: () => {
      setIsEditMode(false);
      reset(); // Also reset the form
    },
  }));

  const dispatch = useDispatch();
  const wbsName = useWbsName();
  const { data: IssueListInDetailModal } = useSelector(
    (state: RootState) => state.IssueListInDetailModal
  );

  // const { selectedEntity } = useSelector(
  //   (state: RootState) => state.workApprovals
  // );

  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const location = useLocation();
  const locationState = location.state || {};
  const projectId = locationState.id ?? locationState.projectId;
  const { projectName, extra } = locationState;

  const [screenSize, setScreenSize] = useState(0);
  const viewChange = () => setScreenSize(window.innerWidth);
  const seletecedIssueDetails = IssueListInDetailModal.selectedIssueDetails;
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [projectTagsList, setProjectTagsList] = useState<any[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [projectUsers, setProjectUsers] = useState<SelectOption[]>([]);

  const [statusOptions, setStatusOptions] = useState<SelectOption[]>([]);
  const [status, setStatus] = useState<TIssueStatus>(
    refreshData?.issueStatus || "Open"
  );

  const [taskDropdownData, setTaskDropdownData] = useState<SelectOption[]>([]);
  const [phaseDropdownData, setPhaseDropdownData] = useState<SelectOption[]>(
    []
  );
  const [taskPhases, setTaskPhases] = useState<SelectOption[]>([]);
  const [options, setOptions] = useState<
    { label: string; value: string | number }[]
  >([]);

  const [disableSubmit, setDisableSubmit] = useState(false);
  const [priorityList, setPriorityList] = useState<SelectOption[]>([]);

  // Approval hierarchy state - matching TaskInformationById.tsx pattern
  const [approvals, setApprovals] = useState<{
    open: boolean;
    data: any[];
  }>({
    open: false,
    data: [],
  });
  const [isApprovalRequired, setIsApprovalRequired] = useState(false);
  const [autoApprovalRemarks, setAutoApprovalRemarks] = useState<string>("");

  const [activeTab, setActiveTab] = useState("comments");

  const TabsList = [
    {
      tabKey: "comments",
      label: "Comments",
    },
    {
      tabKey: "Loghours",
      label: "Log Hours",
    },
    {
      tabKey: "LinkIssue",
      label: "Link Issue",
    },
    {
      tabKey: "Resolution",
      label: "Resolution",
    },
    {
      tabKey: "Documents",
      label: "Documents",
    },
    {
      tabKey: "StatusTimeLine",
      label: "Status Timeline",
    },
    {
      tabKey: "ActivityStream",
      label: "Activity Stream",
    },
  ];

  // const getUserName = (userId?: number | null) => {
  //   const user = projectUsers.find((u) => u.value === userId);
  //   return user?.label ?? "—";
  // };

  const getUserName = (userId?: number | null) => {
    // First try to find the user in projectUsers
    const user = projectUsers.find((u) => u.value === userId);

    // If not found in projectUsers but we have IssueAssignedToName in refreshData, use that
    if (!user?.label && refreshData?.IssueAssignedToName) {
      return refreshData.IssueAssignedToName;
    }

    // Fallback to the user label or dash
    return user?.label ?? "—";
  };

  const methods = useForm<IssueFormFields>({
    mode: "onTouched",
  });

  const parseApiDate = (
    dateString: string | undefined
  ): dayjs.Dayjs | undefined => {
    return dateString ? dayjs(dateString, "YYYY-MM-DD") : undefined;
  };

  useEffect(() => {
    if (refreshData) {
      methods.reset({
        issueName: refreshData.issueName || "",
        issueType: refreshData.issueType || "",
        issueStatus: refreshData.issueStatus || "Open", // Make sure this matches API values
        assignedTo: refreshData.assignedTo || null, // Use the ID, not name
        issueStartDate: parseApiDate(refreshData.issueStartDate),
        deadlineDate: parseApiDate(refreshData.deadlineDate),
        priorityId: refreshData.priorityId || null,
        releasePhaseId: refreshData.releasePhaseId || null,
        linkedTask: refreshData.taskId || null, // Use taskId if linkedTask isn't available
        issueTags: refreshData.issueTags || [],
        issueLinks: refreshData.issueLinks || [],
        reminderFlag: refreshData.reminders?.length > 0,
        flag: refreshData.flag || "",
        description: refreshData.issueDescription || "",
      });

      setTags(
        refreshData.issueTags?.map((tag: any) => ({
          label: tag.tagName,
          value: tag.tagId,
        })) || []
      );

      // Initialize approval hierarchy state
      setIsApprovalRequired(refreshData?.approvalRequired || false);
      setAutoApprovalRemarks(refreshData?.autoApprovalRemarks || "");
      if (
        refreshData?.stateApprovalHierarchy &&
        refreshData.stateApprovalHierarchy.length > 0
      ) {
        const approvalData = refreshData.stateApprovalHierarchy.map(
          (approval: any) => ({
            roleId: approval.roleId,
            level: approval.level,
            userId: approval.userId,
            approvalStage: approval.approvalStage,
            stateApprovalHierarchyId: approval.stateApprovalHierarchyId,
          })
        );
        setApprovals({
          open: false,
          data: approvalData,
        });
      } else {
        setApprovals({
          open: false,
          data: [],
        });
      }
    }
  }, [refreshData, methods.reset]);

  const { control, handleSubmit, setValue, watch, reset } = methods;

  // Exit edit mode when the issue data changes (form is already reset by the effect above with refreshData)
  useEffect(() => {
    setIsEditMode(initialEditMode);
  }, [refreshData, initialEditMode]);

  // If the assignee is changed to someone already chosen as an approver,
  // drop them from the saved approval hierarchy — a user can't approve
  // their own issue. Handles the case where approvals were set before the
  // assignee was picked.
  const watchedAssignee = watch("assignedTo");
  useEffect(() => {
    if (watchedAssignee == null) return;
    setApprovals((prev) => {
      if (!prev.data?.length) return prev;
      const filtered = prev.data.filter(
        (row: any) => Number(row.userId) !== Number(watchedAssignee)
      );
      return filtered.length === prev.data.length
        ? prev
        : { ...prev, data: filtered };
    });
  }, [watchedAssignee]);

  useEffect(() => {
    viewChange();
    window.addEventListener("resize", viewChange);
    return () => window.removeEventListener("resize", viewChange);
  }, []);

  // Fetch initial data: when projectId is set fetch status + project users + tasks; when only issue detail is shown (e.g. no projectId in state) still fetch status so dropdown populates
  useEffect(() => {
    if (projectId && statusOptions.length === 0) {
      fetchStatusOptions();
      fetchProjectUsers();
      fetchTaskDropDownData(projectId);
    } else if (!projectId && refreshData?.issueId && statusOptions.length === 0) {
      fetchStatusOptions();
    }
  }, [projectId, refreshData?.issueId, statusOptions.length]);

  // Fetch issue types when issue detail is shown (same as creation: load when modal/view opens)
  useEffect(() => {
    if (refreshData?.issueId && !isFromWorkUI) {
      fetchIssueTypes();
    }
  }, [refreshData?.issueId, isFromWorkUI]);

  // Ensure current issue type from refreshData is in dropdown options only after full list is loaded
  useEffect(() => {
    const current = refreshData?.issueType;
    if (!current) return;
    setIssueTypeOptions((prev) => {
      if (prev.some((o) => o.value === current)) return prev;
      if (prev.length <= 1) return prev; // don't overwrite initial [None] before fetch completes
      return [{ label: current, value: current }, ...prev];
    });
  }, [refreshData?.issueType]);

  // Fetch additional data when entering edit mode (EDIT MODE ONLY)
  useEffect(() => {
    if (isEditMode && projectId && !isFromWorkUI) {
      fetchProjectTags();
      fetchSeverity();
      fetchPhaseDropDownData(projectId);
    }
  }, [isEditMode, projectId, isFromWorkUI]);

  // Desired order: Open -> InProgress -> Resolved -> UnderQC -> Closed (plus OnHold if from API)
  const ISSUE_STATUS_ORDER = [
    "Open",
    "OnHold",
    "InProgress",
    "Resolved",
    "UnderQC",
    "Closed",
  ];

  const fetchStatusOptions = async () => {
    try {
      const response = await issueApis.getStatus();
      if (response.status && Array.isArray(response.data)) {
        const ordered = ISSUE_STATUS_ORDER.filter((name) =>
          response.data.some(
            (s: string) => s && String(s).toLowerCase() === name.toLowerCase()
          )
        ).map((name) => {
          const fromApi = response.data.find(
            (s: string) => s && String(s).toLowerCase() === name.toLowerCase()
          );
          return { label: fromApi || name, value: fromApi || name };
        });
        const rest = response.data
          .filter(
            (s: string) =>
              !ISSUE_STATUS_ORDER.some(
                (o) => String(o).toLowerCase() === (s || "").toLowerCase()
              )
          )
          .map((status: string) => ({ label: status, value: status }));
        setStatusOptions([...ordered, ...rest]);
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  // State for Issue Type Options
  const [issueTypeOptions, setIssueTypeOptions] = useState<SelectOption[]>([
    { label: "None", value: "None" }, // Initialize with "None"
  ]);

  const fetchIssueTypes = async () => {
    try {
      const response: ApiResponse = await issueApis.issueTypeList();

      if (response.status && Array.isArray(response.data)) {
        const fetchedOptions = response.data.map((item: string) => ({
          label: item,
          value: item,
        }));
        setIssueTypeOptions([
          { label: "None", value: "None" },
          ...fetchedOptions.filter((item) => item.value !== "None"),
        ]);
      } else {
        setIssueTypeOptions([{ label: "None", value: "None" }]);
        messageApi.error("Failed to load issue types or invalid data format.");
      }
    } catch (error) {
      console.error("Error fetching issue types:", error);
      setIssueTypeOptions([{ label: "None", value: "None" }]);
      messageApi.error("Failed to load issue types.");
    }
  };

  const fetchTagSuggestions = async (search: string) => {
    if (!search || search.length < 1) {
      setOptions(
        projectTagsList?.map((tag: any) => ({
          label: tag?.tagName,
          value: tag?.tagId,
        })) || []
      );
      return;
    }
    const simulatedAPIResponse = projectTagsList?.filter((tag) =>
      tag?.tagName?.toLowerCase()?.includes(search?.toLowerCase())
    );
    const formatted = simulatedAPIResponse?.map((tag) => ({
      label: tag?.tagName,
      value: tag?.tagId,
    }));

    if (formatted.length === 0) {
      setOptions([
        {
          label: search,
          value: "newTag__" + search,
        },
      ]);
    } else {
      setOptions(formatted);
    }
  };

  const debouncedFetch = debounce(fetchTagSuggestions, 100);

  const fetchProjectUsers = async () => {
    try {
      const response = await projectsApis.fetchProjectUsers(projectId);
      const {
        data: usersList,
        status,
      }: { data: ProjectUser[]; status: boolean } = response || {};
      if (status) {
        const formatted = usersList?.map((user) => ({
          label: user.userName,
          value: Number(user.userId),
          roleName: user.roleName,
        }));
        setProjectUsers(formatted);
      }
    } catch (error) {
      console.error("Error fetching project users:", error);
      messageApi.error("Failed to fetch project users");
    }
  };

  const fetchIssueById = async () => {
    if (IssueListInDetailModal.id) {
      try {
        const response: any = await issueApis.fetchIssueById(
          IssueListInDetailModal.id as number,
          IssueListInDetailModal?.selectedIssueDetails?.issueStatus,
          Date.now()
          // issueId as number,
          // ""
        );
        // console.log(response, "response from fetchIssueById");

        const { data, status } = response || {};
        if (status) {
          // setRemainders(data.reminders);
          dispatch(
            updateIssueInDetailModalSlice({ selectedIssueDetails: data })
          );
          // ✅ Reset form values here:
          methods.reset({
            issueName: data.issueName || "",
            issueType: data.issueType || "",
            issueStatus: data.issueStatus || "open", // must match IssueFormFields
            issueDescription: data.issueDescription || "",
            assignedTo: data.IssueAssignedToName || "", // or user ID, match your logic
            // issueStartDate: data.issueStartDate
            //   ? dayjs(data.issueStartDate).toDate()
            //   : null,
            // deadlineDate: data.deadlineDate
            //   ? dayjs(data.deadlineDate).toDate()
            //   : null,
            issueStartDate: parseApiDate(data.issueStartDate),
            deadlineDate: parseApiDate(data.deadlineDate),
            priorityId: data.priorityId?.toString() || "", // ensure it's string
            linkedTask: data.taskId || undefined,
            description: data.issueDescription || "",
            issueTags: data.issueTags || [],
            issueLinks: data.issueLinks || [],
            reminderFlag: !!data.reminders?.length,
            // dueDate: data.deadlineDate
            //   ? dayjs(data.deadlineDate).toDate()
            //   : null,
            releasePhaseId: data.releasePhaseId || null,
            severity: data.severity || null,
            // reminders: data.reminders || [],
          });
          setStatus(data.issueStatus || "Open");
          return data;
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    console.log("IssueListInDetailModal.id:useEffect");
    fetchIssueById();
  }, [IssueListInDetailModal.id]);

  useEffect(() => {
    if (projectTagsList?.length) {
      setOptions(
        projectTagsList.map((tag: any) => ({
          label: tag.tagName,
          value: tag.tagId,
        }))
      );
    }
  }, [projectTagsList]);

  const handleIssueLinked = () => {
    setRefreshTrigger((prev) => prev + 1);
    fetchIssueById();
  };

  const fetchProjectTags = async () => {
    try {
      const response: any = await tasksApis.fetchProjectTags(projectId);
      const { data, status } = response || {};
      if (status) {
        setProjectTagsList(
          data?.map((item: any) => ({
            ...item,
            label: item.tagName,
            value: item.tagId,
          }))
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  // API call for tasks dropdown data
  const fetchTaskDropDownData = async (projectId: number) => {
    try {
      const apiResponse = await tasksApis.fetchTaskDropDown({ projectId });
      const { data, status } = apiResponse;
      if (status) {
        let options = data?.map((item: any) => {
          return {
            label: item.taskName,
            value: item.taskId,
          };
        });
        setTaskDropdownData(options);
      } else {
        setTaskDropdownData([]);
      }
    } catch (error) {
      console.error("Failed to fetch tasks:", error);
      setTaskDropdownData([]);
    }
  };

  const fetchSeverity = async () => {
    try {
      const response = await tasksApis.priorityList({ listType: "Severity" });
      const { data, status }: { data: Priority[]; status: boolean } =
        response || {};
      if (status) {
        setPriorityList(
          data?.map((item) => ({
            label: item.priorityName,
            value: item.priorityId,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching priority list:", error);
      messageApi.error("Failed to fetch priority list");
    }
  };

  const fetchTaskByPhases = async (
    linkedTask: number | string | null | undefined,
    setTaskPhases: React.Dispatch<React.SetStateAction<any[]>>
  ) => {
    if (
      !isEditMode ||
      !linkedTask ||
      linkedTask === null ||
      isNaN(Number(linkedTask))
    ) {
      setTaskPhases([]);
      return;
    }
    try {
      const taskId = Number(linkedTask);
      const cachedPhase = taskPhases.find(
        (p) => p.value === refreshData?.releasePhaseId
      );
      if (cachedPhase) return;
      const response = await phaseApis.fetchTaskByPhaseDropDown(taskId);
      const { data, status } = response || {};

      if (status && data) {
        const formatted = [
          {
            label: wbsName(data.phaseName),
            value: data.phaseId,
          },
        ];
        setTaskPhases(formatted);
      } else {
        setTaskPhases([]);
      }
    } catch (error) {
      console.error("Failed to fetch task phases", error);
      setTaskPhases([]);
    }
  };

  const linkedTask = watch("linkedTask");

  useEffect(() => {
    fetchTaskByPhases(linkedTask, setTaskPhases);
  }, [linkedTask, isEditMode]);

  // Modified fetchPhaseDropDownData function
  const fetchPhaseDropDownData = async (projectId: number) => {
    try {
      setLoading(true);
      const apiResponse = await phaseApis.fetchPhaseDropDown({ projectId });
      const { data, status } = apiResponse;
      if (status) {
        const options = data?.map((item: any) => ({
          label: wbsName(item.phaseName),
          value: item.phaseId,
        }));
        setPhaseDropdownData(options);

        // Log for debugging
        // console.log("Fetched phases:", options);
        if (refreshData?.releasePhaseId) {
          const currentPhase = options.find(
            (opt: { value: any }) => opt.value === refreshData.releasePhaseId
          );
          // console.log("Current phase in options:", currentPhase);
        }
      } else {
        setPhaseDropdownData([]);
      }
    } catch (error) {
      console.error("Failed to fetch phase:", error);
      setPhaseDropdownData([]);
    } finally {
      setLoading(false);
    }
  };

  // Add this helper function to check if current user is the assignee
  const isCurrentUserAssignee = (assignedTo: number | null) => {
    return assignedTo === currentUser?.id;
  };

  // helper function to check if current user is admin
  const isCurrentUserAdmin = () => {
    return currentUser?.genericRole?.toLowerCase() === "admin";
  };

  // Status dropdown: enabled only for assignee or admin; disabled for everyone else (including project users).
  const isStatusDropdownDisabled = () => {
    const assignedTo = watch("assignedTo");
    const canChangeStatus =
      statusOptions.length > 0 &&
      !isFromWorkUI &&
      (isCurrentUserAssignee(assignedTo) || isCurrentUserAdmin());
    return !canChangeStatus;
  };

  const handleStatusChange = async ({ key }: { key: string }) => {
    try {
      const newStatus = key as TIssueStatus;
      const currentAssignee = watch("assignedTo");

      // Check permissions
      if (!currentAssignee) {
        throw new Error("Cannot change status - no assignee set");
      }

      if (
        !isCurrentUserAssignee(currentAssignee) &&
        !isCurrentUserAdmin()
      ) {
        throw new Error(
          "Only the assignee or admin can change status"
        );
      }

      const res = await issueApis.changeIssueStatus(
        refreshData.issueId,
        newStatus,
        "" // Empty resolution text as per your requirement
      );

      if (!res?.status) {
        // If response status is false, throw error to enter catch block
        throw new Error(res?.message || "Unknown error occurred");
      }

      setStatus(newStatus);
      await fetchIssueById(); // Refresh the issue data

      Swal.fire({
        icon: "success",
        title: "Status Updated",
        text: res?.message || "Issue status updated successfully.",
        confirmButtonText: "OK",
        customClass: {
          container: "border-class",
        },
      });
    } catch (error: any) {
      console.error("Error changing status:", error);

      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error?.message || "Failed to update issue status.",
        confirmButtonText: "OK",
        customClass: {
          container: "border-class",
        },
      });
    }
  };

  const updateIssue = async (data: any) => {
    try {
      setDisableSubmit(true);
      const formValues = methods.getValues();

      // 1. Validate mandatory fields
      if (
        !formValues.issueName?.trim() ||
        !refreshData.issueId ||
        !refreshData.projectId ||
        formValues.assignedTo === undefined ||
        formValues.assignedTo === null
      ) {
        messageApi.error(
          "Issue Name, Project, Issue ID, and Assignee are required"
        );
        return;
      }

      // 2. Build base payload with REQUIRED fields (as per UpdateIssuePayload interface)
      const payload: UpdateIssuePayload = {
        projectId: refreshData.projectId,
        issueId: refreshData.issueId,
        issueName: formValues.issueName.trim(),
        issueType: formValues.issueType,
        issueDescription: formValues.description?.trim() || "",
        description: formValues.description?.trim() || "",
        issueTags: { newLines: [], deletedLines: [] }, // will update later
        // Add activityId and taskId if they exist
        ...(refreshData.activityId && { activityId: refreshData.activityId }),
        ...(refreshData.taskId && { taskId: refreshData.taskId }),
      };

      // 3. Add OPTIONAL fields ONLY if they have changed

      // Assigned To - only if changed
      if (Number(formValues.assignedTo) !== Number(refreshData.assignedTo)) {
        payload.assignedTo = formValues.assignedTo;
      }

      // Status - only if changed
      if (
        formValues.issueStatus &&
        formValues.issueStatus !== refreshData.issueStatus
      ) {
        payload.issueStatus = formValues.issueStatus;
      }

      // Start Date - only if changed
      const currentStartDate = formValues.issueStartDate?.isValid?.()
        ? formValues.issueStartDate.format("YYYY-MM-DD")
        : null;
      const existingStartDate = refreshData.issueStartDate || null;
      if (currentStartDate !== existingStartDate) {
        if (currentStartDate) {
          payload.issueStartDate = currentStartDate;
        }
      }

      // End Date - only if changed
      const currentEndDate = formValues.deadlineDate?.isValid?.()
        ? formValues.deadlineDate.format("YYYY-MM-DD")
        : null;
      const existingEndDate = refreshData.deadlineDate || null;
      if (currentEndDate !== existingEndDate) {
        if (currentEndDate) {
          payload.deadlineDate = currentEndDate;
        }
      }

      // Priority & Severity - only if changed
      const currentPriorityId = formValues.priorityId
        ? Number(formValues.priorityId)
        : null;
      const existingPriorityId = refreshData.priorityId || null;
      if (currentPriorityId !== existingPriorityId) {
        if (currentPriorityId !== null && currentPriorityId !== undefined) {
          payload.priorityId = currentPriorityId;
          payload.severity = currentPriorityId;
        }
      }

      // Release Phase - only if changed
      const currentPhaseId = formValues.releasePhaseId || null;
      const existingPhaseId = refreshData.releasePhaseId || null;
      if (currentPhaseId !== existingPhaseId) {
        if (currentPhaseId !== null && currentPhaseId !== undefined) {
          payload.releasePhaseId = currentPhaseId;
        }
      }

      // Linked Task - only if changed
      const currentTaskId = formValues.linkedTask || null;
      const existingTaskId = refreshData.taskId || null;
      if (currentTaskId !== existingTaskId) {
        if (currentTaskId !== null && currentTaskId !== undefined) {
          payload.linkedTask = currentTaskId;
        }
      }

      // Flag - only if changed
      if (formValues.flag !== refreshData.flag) {
        payload.flag = formValues.flag;
      }

      // 4. Handle Tags - only if there are changes
      const existingTags = refreshData.issueTags || [];
      const newTags = tags
        .filter(
          (tag: any) =>
            typeof tag.value === "string" ||
            !existingTags.some((et: any) => et.tagId === tag.value)
        )
        .map((tag: any) => ({
          tagName: tag.label,
          ...(typeof tag.value !== "string" && { tagId: Number(tag.value) }),
        }));

      const deletedTags = existingTags
        .filter((et: any) => !tags.some((t: any) => t.value === et.tagId))
        .map((et: any) => ({ issueTagId: Number(et.issueTagId) }));

      // Always include issueTags (required field)
      payload.issueTags = {
        newLines: newTags,
        deletedLines: deletedTags,
      };

      // 5. Handle Approval Hierarchy
      const existingApprovals = refreshData?.stateApprovalHierarchy || [];
      const currentApprovals = approvals.data || [];

      // Find new approval hierarchy entries
      const newApprovals = currentApprovals
        .filter(
          (currentApproval: any) =>
            !currentApproval.stateApprovalHierarchyId ||
            !existingApprovals.some(
              (existingApproval: any) =>
                existingApproval.stateApprovalHierarchyId ===
                currentApproval.stateApprovalHierarchyId
            )
        )
        .map((approval: any) => ({
          userId: Number(approval.userId),
          roleId: Number(approval.roleId),
          level: approval.level,
          approvalStage: approval.approvalStage,
        }));

      // Find updated approval hierarchy entries
      const updatedApprovals = currentApprovals
        .filter((currentApproval: any) =>
          existingApprovals.some(
            (existingApproval: any) =>
              existingApproval.stateApprovalHierarchyId ===
              currentApproval.stateApprovalHierarchyId
          )
        )
        .filter((currentApproval: any) => {
          const existingApproval = existingApprovals.find(
            (ea: any) =>
              ea.stateApprovalHierarchyId ===
              currentApproval.stateApprovalHierarchyId
          );
          return (
            existingApproval &&
            (existingApproval.userId !== currentApproval.userId ||
              existingApproval.roleId !== currentApproval.roleId ||
              existingApproval.level !== currentApproval.level ||
              existingApproval.approvalStage !== currentApproval.approvalStage)
          );
        })
        .map((currentApproval: any) => ({
          stateApprovalHierarchyId: currentApproval.stateApprovalHierarchyId,
          userId: Number(currentApproval.userId),
          roleId: Number(currentApproval.roleId),
          level: currentApproval.level,
          approvalStage: currentApproval.approvalStage,
        }));

      // Find deleted approval hierarchy entries
      const deletedApprovals = existingApprovals
        .filter(
          (existingApproval: any) =>
            !currentApprovals.some(
              (currentApproval: any) =>
                currentApproval.stateApprovalHierarchyId ===
                existingApproval.stateApprovalHierarchyId
            )
        )
        .map((existingApproval: any) => ({
          stateApprovalHierarchyId: existingApproval.stateApprovalHierarchyId,
        }));

      // Include stateApprovalHierarchy if there are any changes
      if (
        newApprovals.length > 0 ||
        updatedApprovals.length > 0 ||
        deletedApprovals.length > 0
      ) {
        payload.stateApprovalHierarchy = {
          ...(newApprovals.length > 0 && { newLines: newApprovals }),
          ...(updatedApprovals.length > 0 && {
            updatedLines: updatedApprovals,
          }),
          ...(deletedApprovals.length > 0 && {
            deletedLines: deletedApprovals,
          }),
        };
        // Always include approvalRequired when there are approval hierarchy changes
        payload.approvalRequired = isApprovalRequired;
      } else if (isApprovalRequired !== refreshData?.approvalRequired) {
        // Include approvalRequired if it has changed (without hierarchy changes)
        payload.approvalRequired = isApprovalRequired;
      }

      // Include autoApprovalRemarks when approval is not required and remarks
      // were entered or changed
      if (
        !isApprovalRequired &&
        autoApprovalRemarks !== (refreshData?.autoApprovalRemarks || "")
      ) {
        payload.autoApprovalRemarks = autoApprovalRemarks;
      }

      // 6. Resolution Tab
      if (
        activeTab === "Resolution" &&
        data.issueResolutions?.newLines?.length
      ) {
        const newResolutions = data.issueResolutions.newLines
          .filter((r: any) => r.resolutionText?.trim())
          .map((r: any) => ({
            resolutionText: r.resolutionText.trim(),
            resolutionStatus: r.resolutionStatus || "Open",
          }));

        if (
          newResolutions.length ||
          data.issueResolutions.deletedLines?.length
        ) {
          payload.issueResolutions = {
            newLines: newResolutions,
            deletedLines: data.issueResolutions.deletedLines || [],
          };
        }
      }

      // 6. Link Issue Tab
      if (activeTab === "LinkIssue" && data.issueLinks?.newLines?.length) {
        const newLinks = data.issueLinks.newLines
          .filter((link: any) => link.linkedIssueId)
          .map((link: any) => ({
            linkedIssueId: Number(link.linkedIssueId),
            linkType: link.linkType || "Related",
          }));

        if (newLinks.length || data.issueLinks.deletedLines?.length) {
          payload.issueLinks = {
            newLines: newLinks,
            deletedLines: data.issueLinks.deletedLines || [],
          };
        }
      }

      // console.log("Final payload:", payload);

      // 7. API call
      const response: ApiResponse = await issueApis.updateIssuebyId(payload);

      if (response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data?.message || "Issue updated successfully",
          confirmButtonText: "OK",
          customClass: {
            container: "border-class",
          },
        });
        setIsEditMode(false);
        await fetchIssueById();
        onBackToSummary?.();
      } else {
        throw new Error(response.message || "Update failed");
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message,
        confirmButtonText: "OK",
        customClass: {
          container: "border-class",
        },
      });
      console.error("Update Issue Error:", error);
    } finally {
      setDisableSubmit(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          position: "relative",
          minHeight: "200px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255, 255, 255, 0.8)",
        }}
      >
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </div>
    );
  }

  const InlineFieldEdit = ({
    label,
    name,
    children,
    isEditMode,
    isRequiredField = false,
    alignTop = false,
  }: {
    label: string;
    name: string;
    children: React.ReactNode;
    isEditMode: boolean;
    isRequiredField?: boolean;
    alignTop?: boolean;
  }) => {
    const {
      getFieldState,
      formState: { errors },
    } = useFormContext();

    const isRequired =
      isEditMode && (!!getFieldState(name)?.isTouched || !!errors[name]);

    const shouldShowStar = isEditMode && isRequiredField;

    return (
      <div
        className={`d-flex ${alignTop ? "align-items-start" : "align-items-center"}`}
        style={{ gap: "12px", minHeight: "40px", fontSize: "13px" }}
      >
        <span
          className="fw-semibold text-dark"
          style={{
            minWidth: 140,
            paddingTop: alignTop ? "6px" : undefined,
          }}
        >
          {label}
          {shouldShowStar && <span className="text-danger ms-1">*</span>}
        </span>
        <div style={{ flex: 1 }}>{children}</div>
      </div>
    );
  };

  const FieldDisplay = ({
    heading,
    value,
    className = "",
  }: {
    heading: string;
    // value: string;
    value: ReactNode;
    className?: string;
  }) => (
    <div className={`d-flex mb-2 ${className}`}>
      <span className="fw-semibold me-2" style={{ minWidth: 160 }}>
        {heading}
      </span>
      <span className="text-muted">{value}</span>
    </div>
  );

  const formatDate = (date?: string | Date | null) =>
    date
      ? new Date(date).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "";

  const getLabel = (options: any[], value: any) =>
    options?.find((opt) => opt.value === value)?.label ?? "";

  const ApprovalTimelineContent = () => {
    const approvalsData = refreshData?.stateApprovalHierarchy;
    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.approvalStatus === "Approved";
              const isRejected = approval.approvalStatus === "Rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.userName}{" "}
                  {approval?.reason && (
                    <Icon
                      name="info"
                      className="ms-1"
                      title={`Reason : ${approval?.reason}`}
                    />
                  )}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.approvalStage && (
                    <div className="designation">{approval?.approvalStage}</div>
                  )}
                  {approval?.level && (
                    <div className="designation">Level: {approval?.level}</div>
                  )}
                  {approval?.approvalStatus && (
                    <div className="designation">
                      Status: {approval?.approvalStatus}
                    </div>
                  )}
                  {approval?.actionDate && (
                    <div className="designation">
                      {dayjs(approval?.actionDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2557d6" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            color: "#2557d6",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          Yes
        </span>
      </Popover>
    );
  };

  // Reject Modal Component (similar to WorkApprovalListView)
  const RejectModal = ({
    isModalVisible,
    setIsModalVisible,
    onReject,
    title = "Reject Item",
    label = "Reason for rejection",
    isLoading = false,
  }: {
    isModalVisible: boolean;
    setIsModalVisible: (visible: boolean) => void;
    onReject: (reason: string) => void;
    title?: string;
    label?: string;
    isLoading?: boolean;
  }) => {
    const [form] = Form.useForm();

    const handleCancel = () => {
      setIsModalVisible(false);
    };

    const handleOk = () => {
      form
        .validateFields()
        .then((values) => {
          const reason = values.reason;
          onReject(reason);
          form.resetFields();
          setIsModalVisible(false);
        })
        .catch((info) => {
          console.log("Validate Failed:", info);
        });
    };

    return (
      <AntModal
        title={title}
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
      >
        <Form form={form} layout="vertical" name="rejectForm">
          <Form.Item
            name="reason"
            label={label}
            rules={[{ required: true, message: "Please input your reason!" }]}
          >
            <Input.TextArea placeholder="Type your reason here..." />
          </Form.Item>
          <div className="actions clearfix">
            <ul>
              <li>
                <Button
                  color="primary"
                  type="submit"
                  onClick={handleOk}
                  disabled={isLoading}
                >
                  {isLoading ? "Submitting..." : "Submit"}
                </Button>
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={handleCancel}
                  className="btn-dim btn btn-secondary"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </li>
            </ul>
          </div>
        </Form>
      </AntModal>
    );
  };

  // Approval/Reject handlers
  const handleApproveReject = async (
    action: "approve" | "reject",
    reason?: string
  ) => {
    if (!approvalData) return;

    setIsProcessing(true);
    try {
      // Cast "Issue" as const to ensure it's treated as the literal type "Issue"
      const baseRequestData = {
        projectId: refreshData.projectId,
        data: [
          {
            approvalLinkId: approvalData.approvalLinkId,
            parentModule: "Issue" as const, // This tells TypeScript it's specifically the "Issue" literal
          },
        ],
      };

      let responce: any;
      if (action === "approve") {
        responce = await workApprovalApis.approveWorkItem(baseRequestData);
      } else {
        if (!reason) throw new Error("Reason is required for rejection");
        responce = await workApprovalApis.rejectWorkItem({
          ...baseRequestData,
          reason: reason,
        });
      }

      const { status, message } = responce;
      if (status) {
        // Success handling
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: message || `Item ${action}d successfully`,
          confirmButtonText: "OK",
          customClass: { container: "border-class" },
        });

        // Refresh issue data instead of closing
        await fetchIssueById();
      } else {
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: message || `Failed to ${action} item.`,
          confirmButtonText: "OK",
        });
      }
    } catch (error: any) {
      // Error handling
      console.error(`Error ${action}ing item:`, error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          error.message ||
          `Failed to ${action} item. Please try again.`,
        confirmButtonText: "OK",
        customClass: { container: "border-class" },
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const approvalData = seletecedIssueDetails;

  const isApprover =
    approvalData?.approvalLinkId && // Check if approval link exists at issue level
    approvalData?.stateApprovals?.some(
      (approval: any) =>
        approval.userId === currentUser?.id && approval.status === "Pending"
    );

  return (
    <>
      <Card
        style={{
          borderRadius: "2px",
          border: "1px solid #f4efefff",
          padding: "0",
        }}
      >
        <div
          style={{
            padding: "0 20px 10px 20px",
            background: "#fff",
            borderRadius: "8px",
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
              gap: "16px",
            }}
          >
            <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2 pe-2 ms-auto">
              {!isEditMode && (
                <li
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Current Status Section */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>CURRENT STATUS:</span>
                      <Dropdown
                        menu={{
                          items: statusOptions.map((item) => ({
                            key: item.value,
                            label: item.label,
                          })),
                          selectedKeys: [status],
                          onClick: handleStatusChange,
                        }}
                        disabled={isStatusDropdownDisabled()}
                      >
                        <a onClick={(e) => e.preventDefault()}>
                          <Space>
                            <Typography.Text
                              style={{ color: "#2557d6", fontWeight: 500 }}
                            >
                              {isApprover
                                ? refreshData?.updates?.issueStatus || status
                                : status}
                            </Typography.Text>
                            {statusOptions && (
                              <DownOutlined style={{ color: "#2557d6" }} />
                            )}
                          </Space>
                        </a>
                      </Dropdown>

                      {isApprover &&
                        refreshData?.updates?.issueStatus &&
                        refreshData?.updates?.issueStatus !==
                          refreshData?.issueStatus && (
                          <Popover
                            content={
                              <div style={{ padding: "8px" }}>
                                <div>
                                  Previous status: {refreshData?.issueStatus}
                                </div>
                              </div>
                            }
                            trigger="hover"
                          >
                            <Icon
                              name="info"
                              style={{
                                cursor: "pointer",
                                color: "#2557d6",
                                marginLeft: "4px",
                              }}
                            />
                          </Popover>
                        )}
                      {!isApprover &&
                        refreshData?.updates?.issueStatus &&
                        refreshData?.updates?.issueStatus !==
                          refreshData?.issueStatus && (
                          <Popover
                            content={
                              <div style={{ padding: "8px" }}>
                                <div>
                                  Requested status:{" "}
                                  {refreshData?.updates?.issueStatus}
                                </div>
                              </div>
                            }
                            trigger="hover"
                          >
                            <Icon
                              name="info"
                              style={{
                                cursor: "pointer",
                                color: "#2557d6",
                                marginLeft: "4px",
                              }}
                            />
                          </Popover>
                        )}
                      {refreshData?.stateApprovalHierarchy?.some(
                        (approval: any) => approval.status === "Rejected"
                      ) &&
                        !isApprover && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              APPROVAL STATUS :
                            </span>
                            <Typography.Text
                              style={{ color: "#a94442", fontWeight: 500 }}
                            >
                              Rejected
                            </Typography.Text>
                            <Popover
                              content={
                                <div style={{ padding: "8px" }}>
                                  {refreshData?.stateApprovalHierarchy
                                    ?.filter(
                                      (approval: any) =>
                                        approval.status === "Rejected"
                                    )
                                    ?.slice(0, 1)
                                    ?.map((approval: any, index: number) => (
                                      <div key={index}>
                                        <div>Status: {approval.status}</div>
                                        {approval.reason && (
                                          <div>Reason: {approval.reason}</div>
                                        )}
                                        <div>By: {approval.userName}</div>
                                        <div>Role: {approval.roleName}</div>
                                        {approval.actionDate && (
                                          <div>
                                            {dayjs(approval.actionDate).format(
                                              "DD-MMM-YYYY"
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              }
                              trigger="hover"
                            >
                              <Icon
                                name="info"
                                style={{
                                  cursor: "pointer",
                                  marginLeft: "4px",
                                  color: "#a94442",
                                }}
                                aria-label="Approval status details"
                              />
                            </Popover>
                          </div>
                        )}
                    </div>

                    {/* Approval Buttons */}
                    {isApprover && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <ApproveAccess moduleName="Issues">
                          <Button
                            style={{
                              backgroundColor: "#d1f5ec",
                              color: "#107154",
                              borderColor: "#d1f5ec",
                            }}
                            onClick={() => handleApproveReject("approve")}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#38d39f";
                              e.currentTarget.style.color = "#fff";
                              e.currentTarget.style.borderColor = "#38d39f";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#d1f5ec";
                              e.currentTarget.style.color = "#107154";
                              e.currentTarget.style.borderColor = "#d1f5ec";
                            }}
                            loading={isProcessing}
                            size="small"
                          >
                            Approve
                          </Button>
                        </ApproveAccess>
                        <CancelAccess moduleName="Issues">
                          <Button
                            style={{
                              backgroundColor: "#fbe3e4",
                              color: "#a94442",
                              borderColor: "#fbe3e4",
                            }}
                            onClick={() => setRejectModalVisible(true)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#e74c3c";
                              e.currentTarget.style.color = "#fff";
                              e.currentTarget.style.borderColor = "#e74c3c";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = "#fbe3e4";
                              e.currentTarget.style.color = "#a94442";
                              e.currentTarget.style.borderColor = "#fbe3e4";
                            }}
                            loading={isProcessing}
                            size="small"
                          >
                            Reject
                          </Button>
                        </CancelAccess>
                      </div>
                    )}

                    {/* More Options Dropdown */}
                    {!isFromWorkUI && (
                      <EditAccess moduleName="Issues">
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="dropdown-toggle btn btn-icon btn-trigger"
                          >
                            <Icon name="more-v" />
                          </DropdownToggle>
                          <DropdownMenu end>
                            <ul className="link-list-opt">
                              <li>
                                <EditAccess moduleName="Issues">
                                  <DropdownItem
                                    tag="a"
                                    href="#editTimeLog"
                                    onClick={(ev) => {
                                      ev.preventDefault();
                                      setIsEditMode(true);
                                    }}
                                    // disabled={!IssueListInDetailModal.allowEdit}
                                  >
                                    <span>Edit</span>
                                  </DropdownItem>
                                </EditAccess>
                              </li>
                            </ul>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </EditAccess>
                    )}
                  </div>
                </li>
              )}

              {isEditMode && (
                <>
                  <li>
                    <Button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      color="primary"
                      onClick={handleSubmit(updateIssue)}
                      disabled={disableSubmit || isFromWorkUI}
                    >
                      <Icon name="save" /> <span>Save</span>
                    </Button>
                  </li>
                  <li>
                    <Button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      color="secondary"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?",
                          text: "You won't be able to revert this!",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Yes, cancel it!",
                          customClass: {
                            container: "border-class",
                          },
                        }).then((result) => {
                          if (result.isConfirmed) {
                            Swal.fire({
                              title: "Cancelled",
                              icon: "success",
                              text: "Your changes have been cancelled.",
                              customClass: {
                                container: "border-class",
                              },
                            });
                            setIsEditMode(false);
                            reset(); // rollback form values
                            onBackToSummary?.();

                            // Reset approval hierarchy
                            setIsApprovalRequired(
                              refreshData?.approvalRequired || false
                            );
                            setAutoApprovalRemarks(
                              refreshData?.autoApprovalRemarks || ""
                            );
                            if (
                              refreshData?.stateApprovalHierarchy &&
                              refreshData.stateApprovalHierarchy.length > 0
                            ) {
                              const approvalData =
                                refreshData.stateApprovalHierarchy.map(
                                  (approval: any) => ({
                                    roleId: approval.roleId,
                                    level: approval.level,
                                    userId: approval.userId,
                                    approvalStage: approval.approvalStage,
                                    stateApprovalHierarchyId:
                                      approval.stateApprovalHierarchyId,
                                  })
                                );
                              setApprovals({
                                open: false,
                                data: approvalData,
                              });
                            } else {
                              setApprovals({
                                open: false,
                                data: [],
                              });
                            }
                          }
                        });
                      }}
                      disabled={isFromWorkUI}
                    >
                      <span>Cancel</span>
                    </Button>
                  </li>
                </>
              )}
            </ul>
          </div>

          {contextHolder}
          <FormProvider {...methods}>
            <form className="content clearfix mt-2">
              <div className="row g-3">
                {/* Issue Name (required) */}
                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Issue Name"
                      name="issueName"
                      isEditMode={isEditMode}
                      isRequiredField={true}
                    >
                      {" "}
                      <FormInput
                        name="issueName"
                        label=""
                        disabled={true || isFromWorkUI}
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Issue Name"
                      // value={(refreshData.issueName, watch("issueName")) || "—"}
                      value={watch("issueName") || refreshData.issueName || "—"}
                      // className={
                      //   (refreshData.issueName, watch("issueName"))
                      //     ? "mb-2 d-flex justify-content-between"
                      //     : "mb-2"
                      // }
                    />
                  )}
                </div>

                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Assignee"
                      name="assignedTo"
                      isEditMode={isEditMode}
                    >
                      <FormSelect
                        name="assignedTo"
                        label=""
                        disabled={
                          !isEditMode ||
                          isFromWorkUI ||
                          // Once an assignee has been set, the field is
                          // read-only. If the issue was created without an
                          // assignee, anyone in edit mode can fill it in.
                          !!refreshData.assignedTo
                        }
                        placeholder="Select Assignee"
                        options={projectUsers}
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Assignee"
                      // value={refreshData.IssueAssignedToName || "—"}
                      value={getUserName(refreshData.assignedTo)}
                    />
                  )}
                </div>

                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Link Task"
                      name="linkedTask"
                      isEditMode={isEditMode}
                      // isRequiredField={true}
                    >
                      <FormSelect
                        name="linkedTask"
                        label=""
                        disabled={isEditMode || isFromWorkUI}
                        options={taskDropdownData}
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Link Task"
                      value={refreshData.taskName || "—"}
                    />
                  )}
                </div>

                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Release Phase"
                      name="releasePhaseId"
                      isEditMode={isEditMode}
                      // isRequiredField={true}
                    >
                      <FormSelect
                        name="releasePhaseId"
                        label=""
                        disabled={isEditMode || isFromWorkUI}
                        options={linkedTask ? taskPhases : phaseDropdownData}
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Release Phase"
                      value={refreshData.releasePhaseName || "—"}
                    />
                  )}
                </div>

                {/* Tags */}
                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Tags"
                      name="tags"
                      isEditMode={isEditMode}
                    >
                      <Select
                        mode="tags"
                        style={{ width: "100%" }}
                        disabled={!isEditMode || isFromWorkUI}
                        placeholder="Type and press enter to create or select a tag"
                        labelInValue
                        value={tags}
                        onChange={(value) => setTags(value)}
                        onSearch={debouncedFetch}
                        options={options}
                        tokenSeparators={[","]}
                        tagRender={tagRender}
                        filterOption={false}
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Tags"
                      value={
                        refreshData.issueTags
                          ?.map((tag: { tagName: any }) => tag.tagName)
                          .join(", ") || "—"
                      }
                    />
                  )}
                </div>

                {/* issue type */}
                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Issue Type"
                      name="issueType"
                      isEditMode={isEditMode}
                      isRequiredField={false}
                    >
                      {" "}
                      <FormSelect
                        name="issueType"
                        label=""
                        disabled={!isEditMode || isFromWorkUI}
                        options={issueTypeOptions}
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Issue Type"
                      value={watch("issueType") || refreshData.issueType || "—"}
                    />
                  )}
                </div>

                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Due Date"
                      name="deadlineDate"
                      isEditMode={isEditMode}
                    >
                      <FormDatePicker
                        name="deadlineDate"
                        label=""
                        disabled={!isEditMode || isFromWorkUI}
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Due Date"
                      value={formatDate(refreshData.deadlineDate)}
                    />
                  )}
                </div>

                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Severity"
                      name="priorityId"
                      isEditMode={isEditMode}
                    >
                      <FormSelect
                        name="priorityId"
                        label=""
                        disabled={!isEditMode || isFromWorkUI}
                        options={priorityList}
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Severity"
                      value={refreshData.priorityName || "—"}
                    />
                  )}
                </div>

                <div style={{ width: "50%", paddingRight: 24 }}>
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Approval Required"
                      name="approvalRequired"
                      isEditMode={isEditMode}
                      isRequiredField={true}
                      alignTop={true}
                    >
                      <div style={{ width: "100%" }}>
                        <Select
                          style={{ width: "100%" }}
                          value={isApprovalRequired ? "yes" : "no"}
                          onChange={(value) => {
                            const newValue = value === "yes";
                            setIsApprovalRequired(newValue);
                            if (!newValue) {
                              setApprovals({
                                open: false,
                                data: [],
                              });
                            }
                          }}
                          disabled={isFromWorkUI}
                          options={[
                            { label: "Yes", value: "yes" },
                            { label: "No", value: "no" },
                          ]}
                        />
                        {isApprovalRequired && (
                          <div style={{ marginTop: "8px" }}>
                            <Popover
                              content="Click here to set required approvals"
                              title="Approval Required"
                            >
                              <a
                                href="#"
                                className="success"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setApprovals((pre) => ({
                                    ...pre,
                                    open: true,
                                  }));
                                }}
                              >
                                <EditOutlined /> Set Approvals{" "}
                                {approvals?.data?.length === 0 ? (
                                  <span className="mandatory">
                                    Please set approvals
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      color: "#38d39f",
                                      fontWeight: 500,
                                    }}
                                  >
                                    ({approvals.data.length} level
                                    {approvals.data.length > 1 ? "s" : ""}{" "}
                                    configured)
                                  </span>
                                )}
                              </a>
                            </Popover>
                          </div>
                        )}
                      </div>
                    </InlineFieldEdit>
                  ) : (
                    <div className="d-flex mb-2">
                      <span
                        className="fw-semibold me-2"
                        style={{ minWidth: 160 }}
                      >
                        Approval Required:
                      </span>
                      <span className="text-muted">
                        {refreshData.approvalRequired ? (
                          <ApprovalTimelineContent />
                        ) : (
                          <div>
                            <Badge color="outline-danger">No</Badge>
                            {refreshData?.autoApprovalRemarks && (
                              <div
                                style={{
                                  marginTop: "4px",
                                  fontSize: "12px",
                                }}
                              >
                                Remarks: {refreshData.autoApprovalRemarks}
                              </div>
                            )}
                          </div>
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {/* Remarks (only when approval is not required) */}
                {isEditMode && !isApprovalRequired && (
                  <div className="col-12">
                    <InlineFieldEdit
                      label="Remarks"
                      name="autoApprovalRemarks"
                      isEditMode={isEditMode}
                      isRequiredField={true}
                      alignTop={true}
                    >
                      <Input.TextArea
                        rows={2}
                        value={autoApprovalRemarks}
                        onChange={(e) =>
                          setAutoApprovalRemarks(e.target.value)
                        }
                        maxLength={500}
                        placeholder="Enter reason for not requiring approvals"
                        disabled={isFromWorkUI}
                      />
                    </InlineFieldEdit>
                  </div>
                )}

                {/* Description */}
                <div className="col-12">
                  {isEditMode ? (
                    <InlineFieldEdit
                      label="Description"
                      name="description"
                      isEditMode={isEditMode}
                    >
                      <FormInput
                        name="description"
                        label=""
                        type="textarea"
                        rows={4}
                        placeholder="Enter description"
                      />
                    </InlineFieldEdit>
                  ) : (
                    <FieldDisplay
                      heading="Description"
                      value={
                        watch("description") ? (
                          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                            {watch("description")}
                          </ReactMarkdown>
                        ) : (
                          "—"
                        )
                      }
                    />
                  )}
                </div>
              </div>
            </form>
          </FormProvider>

          {!hideInternalTabs && (
          <div className="d-flex align-items-center flex-wrap mb-2 mt-2">
            <ul className="nav nav-tabs nav-tabs-card nav-tabs-xs w-100 p-0 m-0 position-relative ">
              {TabsList?.map((_tab: any, index: number) => {
                return (
                  <li
                    className="nav-item"
                    key={_tab?.tabKey}
                    onClick={() => setActiveTab(() => _tab?.tabKey)}
                  >
                    <a
                      onClick={(ev) => {
                        ev.preventDefault();
                      }}
                      className={`nav-link${
                        activeTab === _tab?.tabKey ? " active" : ""
                      }`}
                      href={"#" + _tab?.tabKey}
                    >
                      {_tab?.label}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="tab-content mt-2 w-100">
              <div
                key={"comments"}
                className={`tab-pane${
                  activeTab === "comments" ? " active" : " "
                }`}
              >
                <CommentsTab
                  module="Issue"
                  moduleId={seletecedIssueDetails?.issueId}
                  projectId={projectId}
                  projectName={projectName}
                  name={seletecedIssueDetails?.issueName}
                  isFromWorkUI={isFromWorkUI}
                />
              </div>
              <div
                key={"Loghours"}
                className={`tab-pane${
                  activeTab === "Loghours" ? " active" : " "
                }`}
                style={{
                  padding: "0 16px", // Add horizontal padding
                  overflow: "hidden", // Prevent horizontal scrolling of the entire tab
                }}
              >
                <TimeLogTable
                  projectId={projectId}
                  issueId={seletecedIssueDetails?.issueId || null}
                  activeTab={activeTab}
                  isFromWorkUI={isFromWorkUI}
                  moduleName="Issues"
                  isFromSpecificLevel={true}
                  issueOwnerId={refreshData?.assignedTo}
                  issueOwnerName={refreshData?.IssueAssignedToName}
                />
              </div>

              <div
                key={"LinkIssue"}
                className={`tab-pane${
                  activeTab === "LinkIssue" ? " active" : " "
                }`}
              >
                <LinkIssueTable
                  projectId={projectId}
                  minimalMode={true}
                  issueDetails={refreshData}
                  activeTab={activeTab}
                  isFromWorkUI={isFromWorkUI}
                  onIssueLinked={handleIssueLinked}
                />
              </div>

              <div
                key={"Resolution"}
                className={`tab-pane${
                  activeTab === "Resolution" ? " active" : ""
                }`}
              >
                <IssueResolutionTab
                  activeTab={activeTab}
                  statusOptions={statusOptions} // Pass the status options
                  issueDetails={refreshData}
                  updateIssue={updateIssue}
                  isFromWorkUI={isFromWorkUI}
                />
              </div>
              <div
                key={"Documents"}
                className={`tab-pane${
                  activeTab === "Documents" ? " active" : " "
                }`}
                style={{
                  display: activeTab === "Documents" ? "block" : "none",
                  transition: "opacity 0.3s ease-in-out",
                  opacity: activeTab === "Documents" ? 1 : 0,
                }}
              >
                <DocumentsTabWrapper
                  linkType="issue"
                  shouldLoadData={activeTab === "Documents"}
                  isFromWorkUI={isFromWorkUI}
                />
              </div>
              <div
                key={"StatusTimeLine"}
                className={`tab-pane${
                  activeTab === "StatusTimeLine" ? " active" : ""
                }`}
              >
                <IssueStatusTimeLine activeTab={activeTab} />
              </div>

              <div
                key={"ActivityStream"}
                className={`tab-pane${
                  activeTab === "ActivityStream" ? " active" : ""
                }`}
              >
                <IssueActivityStreamLine activeTab={activeTab} />
              </div>
            </div>
          </div>
          )}
        </div>
      </Card>
      {/* // Add the RejectModal component */}
      <RejectModal
        isModalVisible={rejectModalVisible}
        setIsModalVisible={setRejectModalVisible}
        onReject={(reason) => handleApproveReject("reject", reason)}
        title={`Reject Approval: ${refreshData?.issueName || ""}`}
        label="Reason for rejection"
        isLoading={isRejecting}
      />

      {/* Approval Hierarchy Drawer */}
      <IssueApprovalDrawer
        open={approvals.open}
        projectId={refreshData?.projectId || 0}
        excludeUserId={watch("assignedTo") ?? undefined}
        onClose={() => {
          setApprovals((pre) => ({
            ...pre,
            open: false,
          }));
        }}
        onSubmit={(data: any) => {
          setApprovals({
            open: false,
            data: data,
          });
        }}
        setApprovalSubmissionStatus={(status: boolean) => {}}
        approvalData={approvals.data}
      />
    </>
  );
});

export default IssueInformationById;
